import { getOwner } from "@ember/application"
import Service from "@ember/service"
import { cloneDeep } from "lodash-es"

import { createInsertContext, type DocumentInsertedEvent } from "../lib/document-backends/types"
import type InternalEmberStore from "./internal-ember-store"
import type InternalSupergrainStore from "./internal-supergrain-store"

export default class DocumentReplicator extends Service {
  private internalEmberStore: InternalEmberStore | null = null
  private internalSupergrainStore: InternalSupergrainStore | null = null
  private unregisterListeners: Array<() => void> = []

  start(): void {
    const owner = getOwner(this)
    const internalEmberStore = owner.lookup("service:internal-ember-store") as InternalEmberStore
    const internalSupergrainStore = owner.lookup(
      "service:internal-supergrain-store"
    ) as InternalSupergrainStore

    if (
      this.internalEmberStore === internalEmberStore &&
      this.internalSupergrainStore === internalSupergrainStore &&
      this.unregisterListeners.length > 0
    ) {
      return
    }

    this.stop()

    this.internalEmberStore = internalEmberStore
    this.internalSupergrainStore = internalSupergrainStore

    this.unregisterListeners = [
      this.internalEmberStore.onAfterInsert((event) => this.mirrorInsert(event)),
      this.internalSupergrainStore.onAfterInsert((event) => this.mirrorInsert(event)),
    ]
  }

  stop(): void {
    this.unregisterListeners.forEach((unregister) => unregister())
    this.unregisterListeners = []
  }

  willDestroy(): void {
    this.stop()
    super.willDestroy()
  }

  private mirrorInsert(event: DocumentInsertedEvent): void {
    if (event.context.origin === "replication" || event.context.origin === "patch-create") return

    if (this.internalEmberStore === null || this.internalSupergrainStore === null) return

    const target =
      event.backendId === "ember" ? this.internalSupergrainStore : this.internalEmberStore
    if (!target.canStore(event.doc.type)) return

    target.insertDocument(cloneDeep(event.doc), {
      ...createInsertContext("replication", event.backendId, event.context.transactionId),
    })
  }
}

declare module "@ember/service" {
  interface Registry {
    "document-replicator": DocumentReplicator
  }
}
